import type { MouseEvent } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DialPadIcon } from 'assets/svg';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { useTrackedInteractionAction } from 'features/analytics/hooks';
import { DialPadVariant } from 'features/dial-pad/enums';
import { setDialPadVariantInLocalStorage } from 'features/dial-pad/helpers';
import type { DialPadButtonVariant } from 'features/dial-pad/interfaces';
import {
  dialPadVariantSelector,
  selectShowInputToConsumer,
  setDialPadVariant,
  toggleShowInputToConsumerThunk,
} from 'features/dial-pad/store';
import {
  MenuIconButtonHorizontal,
  MenuPopoverOld,
} from 'features/menu/components';
import { usePopover } from 'features/menu/hooks';
import { isVrsModeSelector } from 'features/multi-mode/store';

export const DialPadSettingsMenuButton = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('translation', { keyPrefix: 'dialPad' });
  const popover = usePopover('dialpad-settings');
  const dialPadVariant = useAppSelector(dialPadVariantSelector);
  const showInputToConsumer = useAppSelector(selectShowInputToConsumer);
  const isVrs = useAppSelector(isVrsModeSelector);

  const { onActionClick: onOpenDialPadSettingsTracking } =
    useTrackedInteractionAction({
      actionName: 'dial-pad-settings-open',
      methodName: 'handleOpenDialPadSettings',
      eventName: 'NavbarInteraction',
    });

  const { onActionClick: onDialPadLayoutChangeTracking } =
    useTrackedInteractionAction({
      actionName: 'dial-pad-layout-change',
      methodName: 'onChangeDialPadOrientation',
      eventName: 'NavbarInteraction',
    });

  const { onActionClick: onShowInputToConsumerTracking } =
    useTrackedInteractionAction({
      actionName: 'dial-pad-show-input-toggle',
      methodName: 'onShowInputToConsumer',
      eventName: 'NavbarInteraction',
    });

  const dialPadButtonsVariant: DialPadButtonVariant[] = useMemo(() => {
    return [
      {
        title: 'phoneDealer',
        variant: DialPadVariant.PHONE_DEALER,
      },
      {
        title: 'calcDialer',
        variant: DialPadVariant.CALCULATOR_DEALER,
      },
    ];
  }, []);

  const onChangeDialPadOrientation = (
    selectedDialPadVariant: DialPadVariant
  ) => {
    dispatch(setDialPadVariant(selectedDialPadVariant));
    setDialPadVariantInLocalStorage(selectedDialPadVariant);
  };

  const onShowInputToConsumer = () => {
    dispatch(toggleShowInputToConsumerThunk());
  };

  const handleOpenDialPadSettings = (event: MouseEvent<HTMLButtonElement>) => {
    popover.handleShowPopover(event);
    onOpenDialPadSettingsTracking();
  };

  return (
    <>
      <MenuIconButtonHorizontal
        testId='dial-pad-layout-options-btn'
        title={t('settingsMenuLabel')}
        icon={<DialPadIcon />}
        isDisabled={false}
        isChecked={false}
        onClickHandler={handleOpenDialPadSettings}
        isCheckIconVisible={false}
        aria-describedby={popover.popoverId}
      />
      <MenuPopoverOld
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        popover={popover}
      >
        {dialPadButtonsVariant.map(
          (dialPadButtonVariant: DialPadButtonVariant) => (
            <MenuIconButtonHorizontal
              testId={`dial-pad-layout-${dialPadButtonVariant.variant}`}
              key={dialPadButtonVariant.title}
              title={t(dialPadButtonVariant.title)}
              isDisabled={false}
              isChecked={dialPadButtonVariant.variant === dialPadVariant}
              onClickHandler={() => {
                onChangeDialPadOrientation(dialPadButtonVariant.variant);
                onDialPadLayoutChangeTracking();
              }}
              isCheckIconVisible
            />
          )
        )}
        {isVrs && (
          <MenuIconButtonHorizontal
            title={t('showInputToConsumer')}
            isDisabled={false}
            isChecked={showInputToConsumer}
            onClickHandler={() => {
              onShowInputToConsumer();
              onShowInputToConsumerTracking();
            }}
            isCheckIconVisible={showInputToConsumer}
          />
        )}
      </MenuPopoverOld>
    </>
  );
};
